import { useEffect, useState } from "react";
import { KeyRound, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../../connect/api";
import { CEO_ORG, Empty, Header, Spinner } from "../ui";

export const AdminPermissions = () => {
  const [items, setItems] = useState(null);
  const [depts, setDepts] = useState([]);
  const [kind, setKind] = useState("staff");
  const [staffQ, setStaffQ] = useState("");
  const [staffOpts, setStaffOpts] = useState([]);
  const [subject, setSubject] = useState("");
  const [subjectLabel, setSubjectLabel] = useState("");
  const [all, setAll] = useState(false);
  const [scope, setScope] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/permissions").then((r) => setItems(r.data.items));
  useEffect(() => { load(); api.get("/admin/overview").then((r) => setDepts(r.data.departments_list)); }, []);
  useEffect(() => {
    if (kind !== "staff" || staffQ.length < 2) { setStaffOpts([]); return; }
    const t = setTimeout(() => api.get("/admin/staff", { params: { q: staffQ, real: 1, limit: 8 } }).then((r) => setStaffOpts(r.data.items)), 250);
    return () => clearTimeout(t);
  }, [staffQ, kind]);

  const grant = async () => {
    if (!subject) { toast.error(kind === "staff" ? "Pick a staff member" : "Pick a department"); return; }
    setBusy(true);
    try {
      await api.post("/admin/permissions", { kind, subject, scope: all ? ["*"] : scope });
      toast.success("Permission granted");
      setSubject(""); setSubjectLabel(""); setStaffQ(""); setScope([]); setAll(false);
      load();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const revoke = async (p) => { await api.delete(`/admin/permissions/${p.id}`); toast.success("Revoked"); load(); };

  return (
    <div className="h-full flex flex-col">
      <Header title="Chat permissions" sub="By default staff can only message their own department" back="/connect/admin" />
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        <section className="nxc-card p-3.5 space-y-3" data-testid="permission-form">
          <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-white/[0.04]">
            {[["staff", "One staff member"], ["department", "Whole department"]].map(([k, l]) => (
              <button key={k} onClick={() => { setKind(k); setSubject(""); setSubjectLabel(""); }} className={`h-9 rounded-lg text-xs font-semibold ${kind === k ? "bg-blue-500/20 text-blue-300" : "text-slate-400"}`} data-testid={`permission-kind-${k}`}>{l}</button>
            ))}
          </div>
          {kind === "staff" ? (
            <div className="relative">
              <input className="nxc-input" placeholder="Search staff by name / email" value={subjectLabel || staffQ} onChange={(e) => { setStaffQ(e.target.value); setSubject(""); setSubjectLabel(""); }} data-testid="permission-staff-search" />
              {staffOpts.length > 0 && !subject && (
                <ul className="absolute z-10 left-0 right-0 mt-1 bg-[#131d31] border border-white/10 rounded-xl overflow-hidden shadow-xl">
                  {staffOpts.map((s) => <li key={s.id}><button onClick={() => { setSubject(s.id); setSubjectLabel(`${s.name} (${s.is_ceo ? CEO_ORG : s.department})`); setStaffOpts([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-white/5" data-testid={`permission-staff-option-${s.id}`}>{s.name} <span className="text-slate-500 text-xs">· {s.department}</span></button></li>)}
                </ul>
              )}
            </div>
          ) : (
            <select className="nxc-input" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="permission-department-select"><option value="">Select department…</option>{depts.map((d) => <option key={d}>{d}</option>)}</select>
          )}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">Can message</div>
            <label className="flex items-center gap-2 text-sm mb-2"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} className="accent-blue-500 w-4 h-4" data-testid="permission-scope-all" /> Every department (company-wide)</label>
            {!all && (
              <div className="flex flex-wrap gap-1.5">
                {depts.map((d) => <button key={d} onClick={() => setScope(scope.includes(d) ? scope.filter((x) => x !== d) : [...scope, d])} className={`nxc-chip ${scope.includes(d) ? "nxc-chip-active" : ""}`} data-testid="permission-scope-chip">{d.replace(" (HR)", "")}</button>)}
              </div>
            )}
          </div>
          <button onClick={grant} disabled={busy} className="nxc-btn nxc-btn-primary w-full h-10" data-testid="permission-grant-button">{busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Grant permission</button>
        </section>
        <section>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 px-1 mb-1.5">Active grants</div>
          {!items ? <div className="flex justify-center py-6"><Spinner /></div> : items.length === 0 ? <Empty icon={KeyRound} title="No cross-department permissions" /> : (
            <ul className="space-y-1.5">
              {items.map((p) => (
                <li key={p.id} className="nxc-card p-3 flex items-center gap-3" data-testid={`permission-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-100 truncate">{p.subject_label}</div>
                    <div className="text-xs text-slate-400">{p.kind === "staff" ? "Staff" : "Department"} → {p.scope.includes("*") ? "every department" : p.scope.join(", ")}</div>
                  </div>
                  <button onClick={() => revoke(p)} className="nxc-btn nxc-btn-danger h-8 text-xs" data-testid={`permission-revoke-${p.id}`}><Trash2 size={12} /> Revoke</button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};
